// ── Contrôleur audio (global) ─────────────────────────────────────────────────
//
// Point de câblage UNIQUE de la fonctionnalité d'écoute (TTS) du frontend :
//   - charge/définit le composant <usito-audio-modal> (une seule fois) ;
//   - enregistre UN seul handler de clic délégué pour toutes les icônes casque,
//     quelles que soient leur origine et leur convention d'identifiant.
//
// Chargement : via l'entry point global usito-paquet.js (toutes les pages), ce qui
// couvre les 4 origines actuelles (recherche dynamique, lexies, exemples, mots).
//
// CONTRAT DE MARKUP (phase 2) — chaque déclencheur, quelle que soit l'origine :
//   <i class="usito-audio icon i-headphones"
//      data-audio-ref="<genre>:<id>"          genre ∈ { lexie, atome }
//      data-audio-origine="recherche|lexies|exemples|mots"
//      title="…"></i>
//   - genre "lexie" : <id> = lexieId → /v2/audio/lexie/{id}/lemme (résolution serveur) ;
//   - genre "atome" : <id> = atomeId → /v2/audio/{id} (atome direct).
// `data-audio-origine` est porté dès maintenant pour la phase 3 (préférences par
// origine) ; le contrôleur ne s'en sert pas encore.

import '../composants/usito-audio-modal.js';
import { lirePrefsAudio, sauvegarderPrefsAudio, ORIGINES_AUDIO } from './prefs.js';

const API_BASE = '/v2';

// ── Préférences par origine ───────────────────────────────────────────────────
// Pose sur <html> la classe `audio-off-<origine>` pour chaque origine DÉSACTIVÉE.
// Le CSS (5_components/_audio.scss) masque alors les icônes de cette origine. Les
// origines activées (défaut) ne reçoivent aucune classe → affichage normal, pas de
// FOUC tant que ce module n'a pas tourné.
function appliquePrefsAudio() {
    const prefs = lirePrefsAudio();
    ORIGINES_AUDIO.forEach(function (origine) {
        document.documentElement.classList.toggle('audio-off-' + origine, prefs[origine] === false);
    });
}

appliquePrefsAudio();
// Réappliquer si les préférences changent dans un autre onglet.
window.addEventListener('storage', function (e) {
    if (e.key === 'usito.prefs.audio') appliquePrefsAudio();
});

// Exposé pour le script inline de préférences.html.
window.lirePrefsAudio        = lirePrefsAudio;
window.sauvegarderPrefsAudio = sauvegarderPrefsAudio;

/**
 * Premier élément précédent (ordre du document) portant l'une des classes fournies.
 * Sert à retrouver le texte (mot/exemple/citation/définition) qui précède l'icône,
 * affiché dans la modale.
 */
function getElementPrécédent(cible, classes) {
    const walker = document.createTreeWalker(document.documentElement, NodeFilter.SHOW_ELEMENT);
    walker.currentNode = cible;
    while (walker.previousNode()) {
        if (classes.some(c => walker.currentNode.classList.contains(c))) {
            return walker.currentNode;
        }
    }
    return null;
}

// Un seul handler délégué pour TOUTES les icônes casque.
document.addEventListener('click', function (e) {
    const déclencheur = e.target.closest('.usito-audio');
    if (!déclencheur) return;

    const ref = déclencheur.dataset.audioRef || '';
    const sep = ref.indexOf(':');
    if (sep < 0) return;
    const genre = ref.slice(0, sep);
    const id    = ref.slice(sep + 1);
    if (!id) return;

    let url, titre, texteHTML;

    if (genre === 'lexie') {
        // Recherche dynamique : id de lexie → forme-lemme résolue côté serveur.
        const li    = déclencheur.closest('li');
        const lemme = li ? li.textContent.trim() : '';
        url       = `${API_BASE}/audio/lexie/${encodeURIComponent(id)}/lemme`;
        titre     = lemme;
        texteHTML = lemme;
    } else if (genre === 'atome') {
        // Pages lexies / exemples / mots : id d'atome direct.
        const container = getElementPrécédent(déclencheur, ['mot', 'exemple', 'citation', 'définition']);
        url       = `${API_BASE}/audio/${encodeURIComponent(id)}`;
        titre     = '';
        texteHTML = container ? container.innerHTML : '';
    } else {
        return; // genre inconnu
    }

    // Un seul exemplaire de modale dans le DOM à la fois.
    const ancienne = document.querySelector('usito-audio-modal');
    if (ancienne) ancienne.remove();

    const modal = document.createElement('usito-audio-modal');
    modal.afficheTranscription = lirePrefsAudio().transcription;
    document.body.appendChild(modal);
    modal.openDepuisUrl(url, titre, texteHTML, id);
});
